import React from 'react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
} from 'recharts';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Button from '../../../../../components/Button';
import Typography from '../../../../../components/Typography';
import Info from '../../../../../components/Info';
import MuiTooltip from '../../../../../components/MuiComponents/MuiTooltip';
import {
  GENPT_COVERAGE,
  genptCoverageOptions,
  owaspApiTop10,
  owaspCoverageScores,
} from '../../../../../utils/constants/genpt';

const COVERAGE_TOOLTIP =
  'Coverage controls how deeply we test your API. Comprehensive runs the full DAST sweep plus IDOR ' +
  'authorization checks; DAST covers common vulnerabilities; IDOR focuses purely on object-level ' +
  'access. Your choice reshapes the OWASP coverage map below.';

// Auth-gated coverage (ROAR-4427): IDOR/Comprehensive both require authenticated access to run
// object-level checks; unauthenticated sessions can only run the DAST sweep.
const AUTH_HINT =
  'Authenticated access is required for Comprehensive and IDOR coverage. Select DAST to continue, ' +
  'or enable Authenticated mode in Configuration.';

// API-6 (Unrestricted Access to Sensitive Business Flows) can't be fully automated — flag it on the
// radar so users know why it's the one axis that stays uncovered.
const API6_TOOLTIP = 'Complex business-logic scenarios may require manual penetration testing';

const LABEL_BY_KEY = owaspApiTop10.reduce((acc, a) => ({ ...acc, [a.key]: a.label }), {});

/**
 * Custom radar axis tick. Pulls cx/cy from recharts and pushes the label LABEL_OFFSET px further
 * out from the chart edge so there is clear breathing room between the polygon and the text.
 * Must clear the danger-badge circle, which sits fixed on the outer-ring vertex: for a top-half
 * vertex the wrapped description lines stack back toward that vertex, so this needs enough radial
 * push to clear a 2-3 line wrap (see API-1) before the badge, not just single-line labels.
 */
const LABEL_OFFSET = 38;

// A category is "covered" by the selected coverage when its OWASP score is at or above this cutoff;
// below it the axis is flagged as a danger (red label + warning badge).
const COVERAGE_DANGER_THRESHOLD = 50;
const DANGER_RED = '#F65C5C';
const API6_TEXT_WIDTH = 34;
const API6_ICON_GAP = 6;

const AxisTick = ({ x, y, cx, cy, radius, textAnchor, payload, scores = {} }) => {
  const code = payload?.value;
  const label = LABEL_BY_KEY[code] || '';
  const words = label.split(' ');
  const lines = [];
  for (let i = 0; i < words.length; i += 2) lines.push(words.slice(i, i + 2).join(' '));

  const isDanger = (scores[code] ?? 0) < COVERAGE_DANGER_THRESHOLD;

  // Radial unit vector (chart centre -> label anchor). The tick anchor (x,y) sits at radius + tick
  // margin, so this direction also points at the outer ring vertex.
  const dx = x - (cx || 0);
  const dy = y - (cy || 0);
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  // Push label radially away from chart centre by LABEL_OFFSET px
  const lx = x + ux * LABEL_OFFSET;
  const ly = y + uy * LABEL_OFFSET;
  // Warning badge sits exactly on the outer ring vertex (the score-100 polygon corner).
  const vx = (cx || 0) + ux * (radius || len);
  const vy = (cy || 0) + uy * (radius || len);

  // API-6 is the only axis this business-logic caveat applies to. The icon sits just past the
  // key text's edge — which side that is depends on the anchor recharts assigned this tick.
  const api6IconX =
    textAnchor === 'end'
      ? lx + API6_ICON_GAP
      : textAnchor === 'middle'
      ? lx + API6_TEXT_WIDTH / 2 + API6_ICON_GAP
      : lx + API6_TEXT_WIDTH + API6_ICON_GAP;
  const api6IconY = ly - 3;

  return (
    <g>
      {isDanger && (
        <g opacity={0}>
          <animate
            attributeName="opacity"
            from="0"
            to="1"
            dur="1.0s"
            calcMode="spline"
            keyTimes="0;1"
            keySplines="0.42 0 0.58 1"
            fill="freeze"
          />
          <circle cx={vx} cy={vy} r={9} fill="none" stroke="#F5C4C5" strokeWidth={1.5} />
          <image href="/images/genpt/api-warning.svg" x={vx - 6} y={vy - 6} width={12} height={12} />
        </g>
      )}
      <text
        x={lx}
        y={ly}
        textAnchor={textAnchor}
        fill={isDanger ? DANGER_RED : '#202020'}
        fontSize={11}
        fontWeight={700}
      >
        {code}
      </text>
      {code === 'API-6' && (
        <MuiTooltip
          title={API6_TOOLTIP}
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#228EBC',
                color: '#fff',
                textAlign: 'center',
                '& .MuiTooltip-arrow': { color: '#228EBC' },
              },
            },
          }}
        >
          <g style={{ cursor: 'pointer' }}>
            <circle cx={api6IconX} cy={api6IconY} r={7} fill="#228EBC" />
            <text
              x={api6IconX}
              y={api6IconY}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={9}
              fontWeight={700}
              fill="#fff"
            >
              i
            </text>
          </g>
        </MuiTooltip>
      )}
      {lines.map((ln, i) => (
        <text
          key={i}
          x={lx}
          y={ly + 13 + i * 11}
          textAnchor={textAnchor}
          fill={isDanger ? DANGER_RED : '#64748B'}
          fontSize={10}
        >
          {ln}
        </text>
      ))}
    </g>
  );
};

/**
 * Coverage selection step: a radio column of coverage options alongside an OWASP radar map
 * that reshapes with the selected option, plus a Continue action.
 */
export default function IDORCoverageStep({
  coverage,
  setCoverage,
  onContinue,
  isLoading = false,
  disabled = false,
  authenticated = false,
}) {
  const scores = owaspCoverageScores[coverage] || {};
  const radarData = owaspApiTop10.map((axis) => ({
    axis: axis.key,
    value: scores[axis.key] || 0,
  }));

  const allowedCoverage = authenticated
    ? [GENPT_COVERAGE.DAST, GENPT_COVERAGE.COMPREHENSIVE, GENPT_COVERAGE.IDOR]
    : [GENPT_COVERAGE.DAST];
  const isAllowed = (value) => allowedCoverage.includes(value);
  const continueDisabled = isLoading || disabled || !isAllowed(coverage);

  return (
    <div className="flex flex-col">
      {/* Heading + info tooltip */}
      <div className="mb-1 flex items-center gap-2">
        <img
          src="/images/genpt/test_type.svg"
          alt="Coverage"
          className="w-6 h-6"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <Typography variant="subtitle1" className="font-semibold text-gray-800">
          Test Coverage
        </Typography>
        <Info tooltip={COVERAGE_TOOLTIP} iconColor="#A1A5B7" />
      </div>
      <Typography variant="body2" className="text-gray-500 mb-4">
        Choose how deeply you want to test this asset
      </Typography>

      {!isAllowed(coverage) && (
        <div className="mb-4 rounded-md border border-[#FCE38A] bg-[#FFF8E1] px-3 py-2 text-sm text-[#8A6D3B]">
          {AUTH_HINT}
        </div>
      )}

      {/* Options (left) + radar chart (right) in one row; stacks on mobile. */}
      <div className="flex flex-col md:flex-row gap-6">
      {/* Coverage radio column — no bg on selected; border-only selection indicator */}
      <div className="flex flex-col gap-3 md:w-1/2">
        {genptCoverageOptions.map((opt) => {
          const selected = coverage === opt.value;
          const optionAllowed = isAllowed(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled || isLoading}
              onClick={() => setCoverage(opt.value)}
              className={`text-left rounded-xl p-4 transition-colors bg-white ${
                disabled || isLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <div className="flex items-start gap-3">
                {selected ? (
                  <RadioButtonCheckedIcon sx={{ color: '#12AFB8', fontSize: 20 }} className="mt-0.5 flex-shrink-0" />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: '#A1A5B7', fontSize: 20 }} className="mt-0.5 flex-shrink-0" />
                )}
                <div className={`min-w-0 ${optionAllowed ? '' : 'opacity-50'}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800">{opt.title}</span>
                    {opt.recommended && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#12AFB81A] text-[#12AFB8]">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{opt.description}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* OWASP radar — fills the right column; outerRadius 62% + generous margins for label room.
          margin.top is taller than the rest so the API-1 top-vertex label never overlaps the
          container edge. */}
      <div className="w-full md:w-1/2">
        <div className="w-full h-[560px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart
              data={radarData}
              outerRadius="62%"
              margin={{ top: 72, right: 88, bottom: 48, left: 88 }}
            >
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="axis" tick={<AxisTick scores={scores} />} />
              <Radar
                dataKey="value"
                stroke="#228EBC"
                fill="#228EBC"
                fillOpacity={0.25}
                dot={{ fill: '#228EBC', stroke: '#fff', strokeWidth: 1, r: 4 }}
                isAnimationActive
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
      </div>

      {/* OWASP threat coverage explainer — full-width below the row */}
      <div className="mt-2">
        <div className="flex items-center gap-1.5 mb-1">
          <img src="/images/genpt/zap-icon.svg" alt="" className="w-[18px] h-[18px]" />
          <span className="text-xs font-semibold uppercase tracking-wide text-[#12AFB8]">
            OWASP Threat Coverage
          </span>
        </div>
        <p className="text-sm text-gray-500">
          This chart shows how your selected coverage aligns with the{' '}
          <span className="font-semibold">OWASP API Security Top 10</span>. Each axis represents a
          vulnerability category; the further outward the shape extends, the deeper the coverage. Inward
          dips indicate reduced coverage based on your selected configuration.
        </p>
      </div>

      {/* Continue — matches the configuration step's button (style + divider + position) */}
      <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />
      <div className="flex flex-wrap justify-end gap-3 sm:gap-5 mt-6">
        <Button
          type="button"
          onClick={() => onContinue?.(coverage)}
          disabled={continueDisabled}
          className={`flex items-center gap-2 px-4 ${
            continueDisabled
              ? '!bg-gray-50 !text-gray-500 cursor-not-allowed'
              : '!bg-primary-500 hover:!bg-primary-600 !text-white cursor-pointer'
          }`}
        >
          {isLoading ? <AutorenewIcon className="animate-spin text-white" /> : 'Continue'}
        </Button>
      </div>
    </div>
  );
}
